/**
 * Two-Tier M365 Consent — backend-sourced URLs.
 *
 * Path 1: Individual User Consent
 *   - User consents for their own M365 data only (no admin required)
 *   - URL built by GET /api/v1/orgs/{orgId}/users/me/m365/authorize
 *
 * Path 2: Organization Admin Consent
 *   - Admin grants consent for the entire organization (full Graph access)
 *   - URL built by POST /api/v1/orgs/{orgId}/consent/admin-url
 *
 * The frontend no longer hand-rolls Microsoft OAuth URLs: the m365 backend owns
 * the OAuth app registration id, the delegated/admin scope sets, the redirect
 * URI, and the server-side CSRF `state` (persisted in its consent-state store,
 * one-time use).
 * This module is a thin transport that returns the ready-to-navigate URL. Both
 * endpoints are [Authorize]d, so the caller must have an authenticated platform
 * session; the admin call is a mutation and carries the in-memory CSRF token.
 *
 * (The OAuth app registration id and Graph scope strings are intentionally NOT
 * present in this file — they live server-side.)
 */

import { getCsrfToken } from '../lib/auth';

// Same-origin API base — the vite dev proxy (local) / nginx /api reverse-proxy
// (prod) forward these to m365. Matches dashboardApi.ts / adminApi.ts.
const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/**
 * Path 1 — individual user consent URL.
 *
 * Delegates to GET /api/v1/orgs/{orgId}/users/me/m365/authorize, which returns a
 * PKCE Microsoft OAuth authorization URL with server-side state. The signed-in
 * user is derived from the session, so no login hint is sent. Requires an
 * authenticated session (endpoint is [Authorize]).
 */
export async function getIndividualConsentUrl(orgId: string): Promise<string> {
  const response = await fetch(
    `${API_BASE}/orgs/${encodeURIComponent(orgId)}/users/me/m365/authorize`,
    {
      credentials: 'include',
      headers: { Accept: 'application/json' },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to get individual consent URL (${response.status})`);
  }

  const data = await response.json();
  const url = data.authorizationUrl ?? data.AuthorizationUrl;
  if (!url) {
    throw new Error('Individual consent URL missing from response');
  }
  return url;
}

/**
 * Path 2 — organization admin consent URL.
 *
 * Delegates to POST /api/v1/orgs/{orgId}/consent/admin-url, which returns an
 * admin-consent URL with server-side state and (after consent) redirects the
 * browser back to `returnUrl`. Requires an authenticated session ([Authorize])
 * and, being a mutation, the session CSRF token.
 *
 * NOTE on `returnUrl`: the backend re-validates it against its own host
 * allowlist and falls back to its configured frontend origin if the host is not
 * allowed. Pass this dashboard's OWN same-origin `/getting-started` (never the
 * aieweb `app.buttonup.ai` host) so consent returns here when the host is allowed.
 */
export async function getAdminConsentUrl(orgId: string, returnUrl?: string): Promise<string> {
  const csrf = getCsrfToken();
  const response = await fetch(
    `${API_BASE}/orgs/${encodeURIComponent(orgId)}/consent/admin-url`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...(csrf ? { 'X-CSRF-Token': csrf } : {}),
      },
      body: JSON.stringify({ returnUrl: returnUrl ?? null }),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to get admin consent URL (${response.status})`);
  }

  const data = await response.json();
  const url = data.consentUrl ?? data.ConsentUrl;
  if (!url) {
    throw new Error('Admin consent URL missing from response');
  }
  return url;
}
